import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
    Dialog,
    IconButton,
    Box,
    Typography,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { AppContext } from '../../App';
import { selectLayoutWidgets } from '../../selectors/widgetDashboard';
import { selectActiveLayoutId } from '../../selectors/home';
import { getSecurityKey } from '../../util/security';
import { convertToUTC, TimeZone } from '../../util/dateTime';
import { formatDateToISODate } from '../../util/general';
import { exportToCSVFile } from '../../util/common';

type ExportChartDataDialogProps = {
    open: boolean;
    onClose: () => void;
};

type SeriesConfig = {
    id: string;
    name?: string;
};

type ChartListItem = {
    secKey: string;
    chartId: string;
    timeBucket: string;
};

const ExportChartDataDialog: React.FC<ExportChartDataDialogProps> = React.memo(({ open, onClose }) => {
    const appContext = useContext(AppContext);
    const activeLayoutId = useSelector(selectActiveLayoutId);
    const widgets = useSelector(selectLayoutWidgets(activeLayoutId));
    const chartWidgets = Object.values(widgets).filter((w: any) => w.widgetType === 'CHART');
    const [selectedChartId, setSelectedChartId] = useState<string>(chartWidgets[0]?.settings?.chartId);
    const [timeFormat, setTimeFormat] = useState<'unix' | 'iso'>('unix');
    const selectedChart = appContext.getChart(selectedChartId);
    const data = useMemo(() => selectedChart?.getPrimaryData() ?? [], [selectedChart]);
    const safeValue = (v: any) => {
        if (v === null || v === undefined) return "";
        if (typeof v === "number" && Number.isNaN(v)) return "";
        if (v === "NaN") return "";
        return v;
    };

    const chartsList = useMemo<ChartListItem[]>(() => {
        return chartWidgets.reduce<ChartListItem[]>((acc, widget: any) => {
            const chartId = widget.settings?.chartId;
            const chart = appContext.getChart(chartId);
            if (!chart) return acc;

            const security = chart.securities?.[0];
            acc.push({
                chartId,
                timeBucket: chart.timeBucket?.name ?? '--',
                secKey: security
                    ? getSecurityKey(security.exchange, security.code)
                    : '--'
            });

            return acc;
        }, []);
    }, [chartWidgets, appContext]);

    const exportToCSV = useCallback(() => {
        if (!selectedChart || data.length === 0) return;

        const headers = ['timestamp', 'open', 'high', 'low', 'close'];

        const seriesGroups = Array.isArray(selectedChart.chartConfig.seriesGroups)
            ? selectedChart.chartConfig.seriesGroups
            : Object.values(selectedChart.chartConfig.seriesGroups || {});

        const seriesArray = Array.isArray(selectedChart.chartConfig.series)
            ? selectedChart.chartConfig.series
            : Object.values(selectedChart.chartConfig.series || {});

        const indicatorGroups = seriesGroups.slice(1);
       

        const seriesMap = indicatorGroups.reduce<Record<string, SeriesConfig[]>>(
            (acc, group: any) => {
                const ids: string[] = group.series || [];
                acc[group.id] = seriesArray.filter(
                    (s: SeriesConfig) => ids.includes(s.id)
                );
                return acc;
            },
            {}
        );
        const indicatorNames: string[] = Object.entries(seriesMap).flatMap(
            ([groupId, seriesList]) => {
                // Find the group object to get its name
                const group = indicatorGroups.find(g => g.id === groupId);
                const groupName = group?.name ?? groupId; // fallback to id if name missing

                return seriesList
                    .map(series => {
                        const chartSeriesData =
                            selectedChart.seriesGroupRef?.[groupId]?.chartSeries?.[series.id]?.getData();

                        if (!chartSeriesData) return null;

                        const isHline =
                            selectedChart.seriesGroupRef?.[groupId]?.chartSeries?.[series.id]?.metadata?.isHline;

                        if (isHline) return null;

                        const hasValidData = data.some(d => {
                            const match = chartSeriesData.find(cd => cd.time === d.time);
                            if (!match || match.series == null) return false;

                            const values = Array.isArray(match.series) ? match.series : [match.series];
                            return values.some(v => safeValue(v) !== "");
                        });

                        return hasValidData ? `${groupName}-${series.name}` : null;
                    })
                    .filter((v): v is string => v !== null);
            }
        );
        console.log(indicatorNames, "jkjhj")


        const allHeaders = [...headers, ...indicatorNames];

        const rows = data.map(d => {
            const timestamp = convertToUTC(d.time as any, TimeZone.UTC);

            const timeValue =
                timeFormat === 'iso'
                    ? formatDateToISODate(new Date(timestamp), '-')
                    : timestamp.toString();
            const baseValues = [
                timeValue,
                safeValue(d.open),
                safeValue(d.high),
                safeValue(d.low),
                safeValue(d.close)
            ];
            const indicatorValues = Object.entries(seriesMap).flatMap(
                ([groupId, seriesList]) =>
                    seriesList.map(series => {
                        const chartSeriesData =
                            selectedChart.seriesGroupRef?.[groupId]
                                ?.chartSeries?.[series.id]
                                ?.getData();

                        if (!chartSeriesData) return "";

                        const isHline =
                            selectedChart.seriesGroupRef?.[groupId]
                                ?.chartSeries?.[series.id]
                                ?.metadata?.isHline;

                        if (isHline) return "";

                        const match = chartSeriesData.find(cd => cd.time === d.time);
                        if (!match || match.series == null) return "";

                        const values = Array.isArray(match.series)
                            ? match.series
                            : [match.series];

                        const cleaned = values.map(v => safeValue(v));

                        return cleaned.every(v => v === "")
                            ? ""
                            : cleaned.join(" ");
                    })
            );

            return [...baseValues, ...indicatorValues].join(',');
        });

        exportToCSVFile('chart_data.csv', allHeaders, rows);
    }, [selectedChart, data, timeFormat]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>

            <Box display="flex" justifyContent="flex-end" sx={{ px: 3, pt: 2 }}>
                <IconButton size="small" onClick={onClose}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 3, pt: 0 }}>
                <Typography fontWeight={500} fontSize="20px">
                    Download chart data
                </Typography>

                <Typography fontSize="0.875rem" color="text.secondary" mt={2} mb={3}>
                    All information from the selected chart, including the symbol & indicators
                    will be saved to a CSV file.
                </Typography>

                <Box mb={3}>
                    <Typography fontSize="0.75rem" color="text.secondary" mb={0.5}>
                        Chart
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={selectedChartId}
                            onChange={e => setSelectedChartId(e.target.value)}
                        >
                            {chartsList.map(item => (
                                <MenuItem key={item.chartId} value={item.chartId}>
                                    {`${item.secKey}, ${item.timeBucket}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box>
                    <Typography fontSize="0.75rem" color="text.secondary" mb={0.5}>
                        Time format (UTC)
                    </Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={timeFormat}
                            onChange={e =>
                                setTimeFormat(e.target.value as 'unix' | 'iso')
                            }
                        >
                            <MenuItem value="unix">UNIX timestamp</MenuItem>
                            <MenuItem value="iso">ISO time</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={exportToCSV}>
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
}
);

export default ExportChartDataDialog;